import type { MouseEvent } from 'react';

import { useMatch } from 'react-router';
import { Box, MuiDialog as Dialog, MuiDrawer as Drawer } from '@/shared/ui';

import { GX_ADDITIONAL_PAPER } from '@/config/colors';

import { Z_INDEX } from '@/config/z-index';
import FactsheetView from '@/features/factsheet/components/FactsheetView';
import {
  FactsheetSelectionContext,
  type FactsheetSelectionState
} from '@/features/factsheet/state/FactsheetSelectionContext';
import { useFactsheetOpenState } from '@/features/factsheet/state/useFactsheetOpenState';
import { useFactsheetOverlayContainer } from '@/features/factsheet/state/useFactsheetOverlayContainer';

interface FactsheetDialogProps {
  selectionState?: FactsheetSelectionState;
}

const FactsheetDialog = ({ selectionState }: FactsheetDialogProps) => {
  const { container } = useFactsheetOverlayContainer();
  const { code, additionalTabs, close, setOpenTabs } = useFactsheetOpenState();
  const isCatalogRoute = Boolean(useMatch('/catalog/*'));

  // On the catalog page, a factsheet requested via the URL (e.g. a shared
  // link) would otherwise flash full-screen before the table finishes
  // loading and claims the overlay container. Wait for that container so it
  // opens straight into the scoped drawer instead.
  const isWaitingForCatalogTable = isCatalogRoute && container === null;
  const open = code !== null && !isWaitingForCatalogTable;

  // Read from the open-state context (optimistic), not raw URL search params,
  // so a newly pushed tab shows immediately instead of waiting for the
  // navigation to settle.
  const factsheetKeys = code ? [code, ...additionalTabs].join(',') : '';

  // Handle backdrop click - close only if clicking directly on the backdrop (not children)
  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>): void => {
    if (event.target === event.currentTarget) {
      close();
    }
  };

  // Don't render anything when closed - this ensures all state is cleared on close
  if (!open) {
    return null;
  }

  const factsheetSelection = (
    <FactsheetSelectionContext value={selectionState ?? null}>
      <FactsheetView
        factsheetKeys={factsheetKeys}
        onDialogClose={close}
        onOpenTabsChange={setOpenTabs}
      />
    </FactsheetSelectionContext>
  );

  // Scoped mode: a right-anchored panel over the claimed container (e.g. the catalog
  // table) instead of a full-viewport modal. Drawer gives us the right-anchor slide
  // transition and backdrop handling natively, so no manual transition/positioning hacks.
  if (container) {
    return (
      <Drawer
        anchor='right'
        container={container}
        // MUI's scroll-lock heuristic measures scrollHeight vs clientHeight on the
        // custom container and pads it to compensate for a scrollbar that actually
        // lives on a nested child, causing the table to visibly shift on open/close.
        disableScrollLock
        // Even transparent, the backdrop spans the whole table (not just the panel)
        // and intercepts pointer events, which blocks scrolling/clicking the table
        // underneath. Removing it keeps the table interactive; closing still works
        // via the panel's close button or Escape (handled on the Drawer root, not
        // the backdrop).
        hideBackdrop
        open={open}
        sx={{
          position: 'absolute',
          zIndex: Z_INDEX.floatingAnchor,
          // The Modal root itself (not just the backdrop) spans the full inset: 0
          // area with default pointer-events, so it blocks the table underneath
          // too unless disabled here and re-enabled only on the visible panel.
          pointerEvents: 'none',
          '& .MuiDrawer-paper': {
            position: 'absolute',
            width: '633px',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            pointerEvents: 'auto',
            borderLeft: '2px solid',
            borderLeftColor: 'primary.main',
            borderBottom: '2px solid',
            borderBottomColor: 'primary.main'
          }
        }}
        onClose={close}
      >
        {factsheetSelection}
      </Drawer>
    );
  }

  return (
    <Dialog
      fullScreen
      open={open}
      sx={{ zIndex: Z_INDEX.menu }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
          backgroundColor: `${GX_ADDITIONAL_PAPER}cc`
        }}
        onClick={handleBackdropClick}
      >
        {factsheetSelection}
      </Box>
    </Dialog>
  );
};

export default FactsheetDialog;
